import { TS_REGEX } from './constants';
import {
  parseMs,
  cleanLyricText,
  isInstrumentalLyric,
  detectSpacingPreference
} from './parser';
import type { ConversionOptions, ConversionResult, TimedLyric } from './types';

/**
 * Converts a merged LRC file (where duplicate lines share timestamps on a single line)
 * into an unmerged, chronologically sorted LRC format.
 *
 * @param rawText Raw LRC string.
 * @param options Optional conversion configuration.
 * @returns Structured ConversionResult with output text and metadata metrics.
 */
export function unmerge(
  rawText: string,
  options: ConversionOptions = {}
): ConversionResult {
  const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();

  const {
    blankHandling = 'merge',
    blankSymbol = '',
    normalizeSpaces = true,
    spacing = 'none'
  } = options;

  const lines = rawText.split(/\r?\n/);
  const headers: string[] = [];
  const timed: TimedLyric[] = [];
  const spacer = detectSpacingPreference(lines, spacing);
  let blankCount = 0;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    const matches = line.match(TS_REGEX);
    if (!matches) {
      headers.push(line);
      continue;
    }

    let lyric = cleanLyricText(line.replace(TS_REGEX, ''), normalizeSpaces);
    const isBlank = isInstrumentalLyric(lyric, blankSymbol);

    if (isBlank) {
      blankCount += matches.length;
      if (blankHandling === 'remove') {
        continue;
      }
      lyric = blankSymbol ? blankSymbol : lyric;
    }

    for (const ts of matches) {
      timed.push({
        ms: parseMs(ts),
        ts,
        lyric,
        isBlank
      });
    }
  }

  // Stable chronological sort by timestamp millisecond
  timed.sort((a, b) => a.ms - b.ms);

  const outputLines = headers.concat(
    timed.map((t) => {
      if (!t.lyric) return t.ts;
      let s = spacer;
      if (t.isBlank && !s && spacing !== 'none') {
        s = ' ';
      }
      return `${t.ts}${s}${t.lyric}`;
    })
  );

  const endTime = typeof performance !== 'undefined' ? performance.now() : Date.now();

  return {
    text: outputLines.join('\n'),
    inputLines: lines.filter((l) => l.trim()).length,
    outputLines: outputLines.length,
    blankCount,
    durationMs: Math.max(0, Math.round((endTime - startTime) * 100) / 100)
  };
}
